import React from 'react';
import { useRouteMatch, Link, withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';

import { useGetPrintTemplatesByIdQuery,
  useGetSMSTemplatesByIdQuery,
  useGetEmailTemplatesByIdQuery,
  useGetImageByIdQuery,
  useGetAllCampaignsQuery } from '../../apis/backend';

const routes = [
  {
    path: '/',
    title: 'dashboard'
  },
  {
    path: '/campaigns',
    title: 'campaigns'
  },
  {
    path: '/campaigns/:id',
    title: { param: 'id', entityType: 'campaign'}
  },
  {
    path: '/templates',
    title: 'templates'
  },
  {
    path: '/templates/sms',
    title: 'SMS'
  },
  {
    path: '/templates/sms/add',
    title: 'add-sms-template'
  },
  {
    path: '/templates/sms/:id',
    title: { param: 'id', entityType: 'sms' },
  },
  {
    path: '/templates/email',
    title: 'email'
  },
  {
    path: '/templates/email/add',
    title: 'add-email-template'
  },
  {
    path: '/templates/email/:id',
    title: { param: 'id', entityType: 'email' },
  },
  {
    path: '/templates/print',
    title: 'print'
  },
  {
    path: '/templates/print/add',
    title: 'Add'
  },
  {
    path: '/templates/print/:id(\\d+)',
    title: { param: 'id', entityType: 'print' },
  },
  {
    path: '/imagebank',
    title: 'imagebank',
  },
  {
    path: '/imagebank/print',
    title: 'print'
  },
  {
    path: '/imagebank/email',
    title: 'email',
  },
  {
    path: '/imagebank/:imageType',
    title: { param: 'imageType' },
  },
  {
    path: '/imagebank/:imageType/:id(\\d+)',
    title: { param: 'id', entityType: 'image'},
  },
  {
    path: '/imagebank/add',
    title: 'add-image',
  }
];

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    alignSelf: 'center',
    height: '100%',
    whiteSpace: 'nowrap',
    marginRight: theme.spacing(5),
    '& div': {
      display: 'flex',
      alignSelf: 'center',
    }
  },
  breadcrumbSeparator: {
    color: theme.palette.grey[600],
    margin: theme.spacing(0, 1)
  },
}));

const Breadcrumb = (props) => {
  const { match } = props;

  const { t } = useTranslation();
  const classes = useStyles();

  const chainId = useSelector(state => state.doris.selectedChain);
  const siteId = useSelector(state => state.doris.selectedSite);

  const skipGetAllCampaigns = !(chainId && siteId);
  /* eslint-disable no-unused-vars */
  const { data: campaignList, isLoading: isLoadingCampaigns, error: errorCampaigns } = useGetAllCampaignsQuery({ chainId, siteId }, { skip: skipGetAllCampaigns });

  const separator = props.separator || <KeyboardArrowRightIcon className={classes.breadcrumbSeparator} />;

  const dashboardMatch = useRouteMatch('/dashboard');
  const campaignAllMatch = useRouteMatch('/campaigns');
  const campaignMatch = useRouteMatch('/campaigns/:id');
  const templatesMatch = useRouteMatch('/templates');
  const smsAllTemplatesMatch = useRouteMatch('/templates/sms');
  const smsAddTemplateMatch = useRouteMatch('/templates/sms/add');
  const smsTemplateMatch = useRouteMatch('/templates/sms/:id');
  const emailAllTemplatesMatch = useRouteMatch('/templates/email');
  const emailTemplateMatch = useRouteMatch('/templates/email/:id');
  const emailAddTemplateMatch = useRouteMatch('/templates/email/add');
  const imagebankMatch = useRouteMatch('/imagebank');
  const imagebankAddMatch = useRouteMatch('/imagebank/add');
  const printImagebankMatch = useRouteMatch('/imagebank/print');
  const printImageMatch = useRouteMatch('/imagebank/:imageType/:id(\\d+)');
  const emailImagebankMatch = useRouteMatch('/imagebank/email');
  const emailImageMatch = useRouteMatch('/imagebank/email/:id');
  const printAllTemplateMatch = useRouteMatch('/templates/print');
  const printTemplateMatch = useRouteMatch('/templates/print/:id(\\d+)');
  const printNewTemplateMatch = useRouteMatch('/templates/print/add');

  let skipGetPrintTemplate = true, skipGetSMSTemplate = true, skipGetEmailTemplate = true, skipGetImage = true;

  let matchedRoute;
  if (dashboardMatch && dashboardMatch.isExact) {
    matchedRoute = dashboardMatch;
  } else if (campaignAllMatch && campaignAllMatch.isExact) {
    matchedRoute = campaignAllMatch;
  } else if (campaignMatch && campaignMatch.isExact) {
    matchedRoute = campaignMatch;
  } else if (templatesMatch && templatesMatch.isExact) {
    matchedRoute = templatesMatch;
  } else if (smsAllTemplatesMatch && smsAllTemplatesMatch.isExact) {
    matchedRoute = smsAllTemplatesMatch;
  } else if (smsAddTemplateMatch && smsAddTemplateMatch.isExact) {
    matchedRoute = smsAddTemplateMatch;
  } else if (smsTemplateMatch && smsTemplateMatch.isExact) {
    matchedRoute = smsTemplateMatch;
    const route = routes.find(route => route.title.entityType == 'sms');
    skipGetSMSTemplate = route.path != matchedRoute.path;
  } else if (emailAllTemplatesMatch && emailAllTemplatesMatch.isExact) {
    matchedRoute = emailAllTemplatesMatch;
  } else if (emailAddTemplateMatch && emailAddTemplateMatch.isExact) {
    matchedRoute = emailAddTemplateMatch;
  } else if (emailTemplateMatch && emailTemplateMatch.isExact) {
    matchedRoute = emailTemplateMatch;
    const route = routes.find(route => route.title.entityType == 'email');
    skipGetEmailTemplate = route.path != matchedRoute.path;
  } else if (imagebankMatch && imagebankMatch.isExact) {
    matchedRoute = imagebankMatch;
  } else if (imagebankAddMatch && imagebankAddMatch.isExact) {
    matchedRoute = imagebankAddMatch;
  } else if (printImagebankMatch && printImagebankMatch.isExact) {
    matchedRoute = printImagebankMatch;
  } else if (printImageMatch && printImageMatch.isExact) {
    matchedRoute = printImageMatch;
    const route = routes.find(route => route.title.entityType == 'image');
    skipGetImage = route.path != matchedRoute.path;
  } else if (emailImagebankMatch && emailImagebankMatch.isExact) {
    matchedRoute = emailImagebankMatch;
  } else if (emailImageMatch && emailImageMatch.isExact) {
    matchedRoute = emailImageMatch;
  } else if (printAllTemplateMatch && printAllTemplateMatch.isExact) {
    matchedRoute = printAllTemplateMatch;
  } else if (printTemplateMatch && printTemplateMatch.isExact) {
    matchedRoute = printTemplateMatch;
    const route = routes.find(route => route.title.entityType == 'print');
    skipGetPrintTemplate = route.path != matchedRoute.path;
  } else if (printNewTemplateMatch && printNewTemplateMatch.isExact) {
    matchedRoute = printNewTemplateMatch;
  } else {
    matchedRoute = match;
  }

  const id = matchedRoute.params.id;
  const type = matchedRoute.params?.imageType;
  const { data: printTemplate, isLoading: isLoadingPrint, isFetchingPrint }
    = useGetPrintTemplatesByIdQuery(id, { skip: skipGetPrintTemplate });

  const { data: emailTemplate, isLoading: isLoadingEmail, isFetchingEmail }
  = useGetEmailTemplatesByIdQuery(id, { skip: skipGetEmailTemplate });

  const { data: smsTemplate, isLoading: isLoadingSMS, isFetchingSMS }
    = useGetSMSTemplatesByIdQuery(id, { skip: skipGetSMSTemplate });

  const { data: image, isLoading: isLoadingImage, isFetchingImage }
    = useGetImageByIdQuery({ id, type }, { skip: skipGetImage });

  const getEntityTitle = (entityType, entityId) => {
    try {
      let entity;
      switch (entityType) {
      case 'campaign':
        if (campaignList?.length) {
          entity = campaignList?.find(t => t.id == entityId);
          return campaignList?.length ? entity.name : '';
        } else {
          return '';
        }
      case 'sms':
        return entityId == smsTemplate?.id && !isFetchingSMS && !isLoadingSMS ? smsTemplate?.title : '';
      case 'email':
        return entityId == emailTemplate?.id && !isFetchingEmail && !isLoadingEmail ? emailTemplate?.title : '';
      case 'print':
        return !isFetchingPrint && printTemplate?.title;
      case 'image':
        return entityId == image?.id && !isLoadingImage && !isFetchingImage ? image?.file_name : '';
      default:
        return;
      }
    } catch (err) {
      console.log(err);
    }
  };

  const extractPaths = (path) => {
    const splitPath = path === '/' ? ['/'] : path.split('/');
    const numOfPathsContained = splitPath.length;
    if (numOfPathsContained === 1) return ['/'];

    const paths = [];
    for (let i = 0; i < numOfPathsContained; i++) {
      const limitOfCurrentPath = i + 1;
      let currentPath = splitPath
        .slice(0, limitOfCurrentPath)
        .reduce((accumulator, current, index) => {
          if (index === 0) return '/';
          return index === 1
            ? accumulator + current
            : accumulator + '/' + current;
        }, '');

      paths.push(currentPath);
    }

    return paths;
  };

  const buildBreadcrumbItems = (match, paths, routes) => {
    const items = paths.map((currentPath, index) => {
      let route = routes.find(r => r.path == currentPath);
      if (!route) return '';
      const  { entityType } = route.title;
      const { id } = match.params;
      let title;
      if (route.title.param) {
        title = route.title?.entityType
          ? getEntityTitle(entityType, id)
          : t(matchedRoute.params[route.title.param]);
      } else {
        title = t(route.title);
      }
      const url = pathToUrl(match, currentPath);

      let currentItem = (index + 1) !== paths.length
        ? <><Link key={index} to={url}>{title}</Link>{separator}</>
        : title;

      return (
        <Box key={'s-' + index} display="flex">
          {currentItem}
        </Box>
      );
    });

    return items;
  };

  const pathToUrl = (match, route) => {
    const params = Object.keys(match.params);
    return params.reduce((acc, curr) => {
      return acc.replace(':' + curr, match.params[curr]);
    }, route);
  };

  const breadcrumb = buildBreadcrumbItems(matchedRoute, extractPaths(matchedRoute.path), routes);

  return (
    <Box className={classes.root} display="flex">
      {separator}{breadcrumb}
    </Box>
  );
};


export default withRouter(Breadcrumb);
